import { Action, Middleware } from '@reduxjs/toolkit';

import { reset, triggerPlatformLogout } from './actions';

import type { Persistor } from 'redux-persist';


// Structural shape of the RTK Query api object the middleware needs. Avoids
// pulling mboAPI (and its transitive imports) into the factory module so this
// file stays trivially testable.
interface ApiResetUtility {
  util: { resetApiState: () => Action };
}

interface PlatformLogoutMiddlewareOptions {
  // Lazy getter because persistStore() needs the store, which needs this
  // middleware — the closure is the only way to resolve the cycle.
  getPersistor: () => Persistor;
  baseApi: ApiResetUtility;
  // Optional async side effect for targets that need to tear down native SDKs
  // (e.g. mobile invokes LucraSDK.logout()). On web there is no such SDK.
  onPlatformLogout?: () => Promise<void>;
}

// Catches `triggerPlatformLogout` and `reset` so the awaited promise in
// baseApiWithReauth.logout() actually resolves and persisted state is purged.
// Mounting this on a store is required for the auth refresh-failure flow to
// complete; without it, the mutex inside baseQueryWithReauth never releases
// and dependent queries (e.g. getSimulatorLocations) hang forever — see GF-657.
export const createPlatformLogoutMiddleware = ({
  getPersistor,
  baseApi,
  onPlatformLogout,
}: PlatformLogoutMiddlewareOptions): Middleware => ({ dispatch }) => (next) => async (action) => {
    // next(action) is invoked first so the reducer state for the action is
    // applied before the persistor.purge()/flush() operations run.
    const result = next(action);

    if (triggerPlatformLogout.match(action)) {
      if (onPlatformLogout) {
        await onPlatformLogout();
      }
      action?.payload?.onSuccess?.();
    }

    if (action.type === reset.type) {
      dispatch(baseApi.util.resetApiState());
      const persistor = getPersistor();
      await persistor.purge();
      await persistor.flush();
    }

    return result;
  };
